// Cadena de frío: temperatura del viaje por despacho (producción→carga→tránsito→
// entrega) evaluada contra bandas seguras + certificado de inocuidad. Catering →
// Logística → Cadena de frío.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Thermometer, ShieldCheck, ShieldAlert, Plus, Cog, QrCode, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering, hhmm } from './shared';

interface Desp { despacho_id: string; codigo: string; fecha: string; ruta: string | null; estado: string; veredicto: string; n_lecturas: number; violaciones: number }
interface Lectura { hora: string | null; momento: string; tipo: string; temp_c: number; origen: string; contrato: string | null; en_banda: boolean }
interface Cert { despacho_id: string; codigo: string; fecha: string; ruta: string | null; placa: string | null; lecturas: Lectura[]; n_lecturas: number; violaciones: number; temp_min: number | null; temp_max: number | null; veredicto: string; config: { caliente_min_c: number; frio_max_c: number } }

const MOM: Record<string, string> = { produccion: 'Producción', carga: 'Carga', transito: 'Tránsito', entrega: 'Entrega' };
const VER: Record<string, { cls: string; txt: string; label: string }> = {
  integra: { cls: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20', txt: 'text-emerald-700 dark:text-emerald-400', label: 'Cadena íntegra' },
  comprometida: { cls: 'border-red-500 bg-red-50 dark:bg-red-900/20', txt: 'text-red-700 dark:text-red-400', label: 'Cadena comprometida' },
  sin_datos: { cls: 'border-muted bg-muted/40', txt: 'text-muted-foreground', label: 'Sin datos' },
};

export function CadenaFrioCatering() {
  const qc = useQueryClient();
  const [selId, setSelId] = useState('');
  const [config, setConfig] = useState(false);
  const [reg, setReg] = useState<{ momento: string; tipo: string; temp: string; notas: string } | null>(null);

  const { data: despachos = [] } = useQuery<Desp[]>({ queryKey: ['cat-cadena-despachos'], queryFn: () => catRpc<Desp[]>('cat_cadena_despachos', {}) });
  const sel = despachos.find((d) => d.despacho_id === selId) ?? despachos[0];
  const did = sel?.despacho_id ?? '';

  const { data: cert } = useQuery<Cert>({ queryKey: ['cat-cadena-cert', did], queryFn: () => catRpc<Cert>('cat_cadena_certificado', { _despacho_id: did }), enabled: !!did });
  const { data: cfg } = useQuery<{ caliente_min_c: number; frio_max_c: number }>({ queryKey: ['cat-cadena-cfg'], queryFn: () => catRpc('cat_cadena_config_get', {}) });

  const registrar = useMutation({
    mutationFn: (r: { momento: string; tipo: string; temp: string }) => catRpc('cat_cadena_registrar', { _despacho_id: did, _momento: r.momento, _tipo: r.tipo, _temp_c: Number(r.temp) }),
    onSuccess: () => { invalidateCatering(qc); setReg(null); toast.success('Lectura registrada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const guardarCfg = useMutation({
    mutationFn: (c: { cal: number; frio: number }) => catRpc('cat_cadena_config_set', { _caliente_min_c: c.cal, _frio_max_c: c.frio }),
    onSuccess: () => { invalidateCatering(qc); toast.success('Bandas actualizadas'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const v = cert ? VER[cert.veredicto] ?? VER.sin_datos : VER.sin_datos;
  const certUrl = did ? `${window.location.origin}/cadena/${did}` : '';

  return (
    <div className="grid gap-4 p-4 lg:grid-cols-[300px_1fr]">
      {/* Despachos */}
      <Card className="h-fit">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-sm"><span className="flex items-center gap-2"><Thermometer className="h-4 w-4" /> Despachos</span>
            <button onClick={() => setConfig((c) => !c)} title="Bandas" className="text-muted-foreground hover:text-foreground"><Cog className="h-4 w-4" /></button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {config && cfg && (
            <div className="mb-2 rounded-md border p-2 text-xs">
              <p className="mb-1 font-medium">Bandas seguras (°C)</p>
              <BandEditor cal={cfg.caliente_min_c} frio={cfg.frio_max_c} saving={guardarCfg.isPending} onSave={(cal, frio) => guardarCfg.mutate({ cal, frio })} />
            </div>
          )}
          {despachos.map((d) => {
            const dv = VER[d.veredicto] ?? VER.sin_datos;
            return (
              <button key={d.despacho_id} onClick={() => setSelId(d.despacho_id)}
                className={`w-full rounded-md border p-2 text-left text-sm transition hover:border-primary ${did === d.despacho_id ? 'border-primary ring-1 ring-primary' : ''}`}>
                <div className="flex items-center justify-between"><span className="font-medium">{d.codigo}</span>
                  <span className={`rounded-full border px-1.5 text-[10px] ${dv.cls} ${dv.txt}`}>{d.veredicto === 'integra' ? '✓' : d.veredicto === 'comprometida' ? '✗' : '—'}</span></div>
                <div className="text-[11px] text-muted-foreground">{d.fecha} · {d.ruta ?? 's/ruta'} · {d.n_lecturas} lecturas</div>
              </button>
            );
          })}
          {!despachos.length && <p className="text-sm text-muted-foreground">Sin despachos recientes.</p>}
        </CardContent>
      </Card>

      {/* Certificado */}
      <div className="space-y-4">
        {cert && (
          <>
            <Card className={`border-2 ${v.cls}`}>
              <CardContent className="flex flex-wrap items-center gap-4 py-3">
                {cert.veredicto === 'comprometida' ? <ShieldAlert className={`h-8 w-8 ${v.txt}`} /> : <ShieldCheck className={`h-8 w-8 ${v.txt}`} />}
                <div>
                  <p className={`text-lg font-semibold ${v.txt}`}>{v.label}</p>
                  <p className="text-xs text-muted-foreground">{cert.codigo} · {cert.fecha} · {cert.ruta ?? 's/ruta'}{cert.placa ? ` · ${cert.placa}` : ''}</p>
                </div>
                <div className="ml-auto flex gap-4 text-sm">
                  <div><p className="text-[11px] uppercase text-muted-foreground">Lecturas</p><p className="font-semibold">{num(cert.n_lecturas, 0)}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">Fuera de banda</p><p className={`font-semibold ${cert.violaciones > 0 ? 'text-red-600' : ''}`}>{num(cert.violaciones, 0)}</p></div>
                  <div><p className="text-[11px] uppercase text-muted-foreground">Rango °C</p><p className="font-semibold">{cert.temp_min ?? '—'}–{cert.temp_max ?? '—'}</p></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-sm">Línea de tiempo
                  <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs" onClick={() => setReg({ momento: 'transito', tipo: 'caliente', temp: '', notas: '' })}><Plus className="h-3.5 w-3.5" /> Lectura</Button>
                </CardTitle>
                <p className="text-xs text-muted-foreground">Banda: caliente ≥ {cert.config.caliente_min_c}°C · frío ≤ {cert.config.frio_max_c}°C.</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Hora</th><th className="py-1 pr-2">Momento</th><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2 text-right">°C</th><th className="py-1 pr-2">Origen</th><th className="py-1 pr-2 text-center">Estado</th></tr></thead>
                    <tbody>
                      {cert.lecturas.map((l, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 whitespace-nowrap">{hhmm(l.hora)}</td>
                          <td className="py-1.5 pr-2">{MOM[l.momento] ?? l.momento}{l.contrato ? <span className="block text-[11px] text-muted-foreground">{l.contrato}</span> : null}</td>
                          <td className="py-1.5 pr-2 text-xs">{l.tipo}</td>
                          <td className={`py-1.5 pr-2 text-right font-mono ${!l.en_banda ? 'font-semibold text-red-600' : ''}`}>{num(l.temp_c, 1)}</td>
                          <td className="py-1.5 pr-2 text-xs text-muted-foreground">{l.origen}</td>
                          <td className="py-1.5 pr-2 text-center">{l.en_banda ? <Check className="mx-auto h-4 w-4 text-emerald-600" /> : <X className="mx-auto h-4 w-4 text-red-600" />}</td>
                        </tr>
                      ))}
                      {!cert.lecturas.length && <tr><td colSpan={6} className="py-3 text-sm text-muted-foreground">Sin lecturas. Registra la temperatura de salida y del tránsito.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Certificado público */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><QrCode className="h-4 w-4" /> Certificado verificable</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap items-center gap-4">
                <div className="rounded-md bg-white p-2"><QRCodeSVG value={certUrl} size={120} /></div>
                <div className="text-xs"><p className="break-all font-mono text-muted-foreground">{certUrl}</p>
                  <a className="mt-1 inline-block text-primary underline" href={certUrl} target="_blank" rel="noreferrer">Abrir certificado</a>
                  <p className="pt-1 text-muted-foreground">Compártelo con el cliente como prueba de inocuidad de este despacho.</p></div>
              </CardContent>
            </Card>
          </>
        )}
        {!cert && <Card><CardContent className="py-6 text-sm text-muted-foreground">Selecciona un despacho.</CardContent></Card>}

        {/* Registrar lectura */}
        {reg && (
          <Card className="border-primary">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Registrar lectura</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap items-end gap-2">
              <div><label className="block text-xs text-muted-foreground">Momento</label>
                <select className={`${fieldCls} w-40`} value={reg.momento} onChange={(e) => setReg({ ...reg, momento: e.target.value })}>
                  {['produccion','carga','transito','entrega'].map((m) => <option key={m} value={m}>{MOM[m]}</option>)}
                </select></div>
              <div><label className="block text-xs text-muted-foreground">Tipo</label>
                <select className={`${fieldCls} w-28`} value={reg.tipo} onChange={(e) => setReg({ ...reg, tipo: e.target.value })}>
                  <option value="caliente">caliente</option><option value="frio">frío</option>
                </select></div>
              <div><label className="block text-xs text-muted-foreground">°C</label><input type="number" step="0.1" className={`${fieldCls} w-24`} value={reg.temp} onChange={(e) => setReg({ ...reg, temp: e.target.value })} /></div>
              <Button size="sm" className="gap-1" disabled={registrar.isPending || reg.temp === ''} onClick={() => registrar.mutate(reg)}><Check className="h-4 w-4" /> Guardar</Button>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setReg(null)}><X className="h-4 w-4" /> Cancelar</Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function BandEditor({ cal, frio, onSave, saving }: { cal: number; frio: number; onSave: (cal: number, frio: number) => void; saving: boolean }) {
  const [c, setC] = useState(String(cal));
  const [f, setF] = useState(String(frio));
  const changed = Number(c) !== cal || Number(f) !== frio;
  return (
    <div className="flex items-end gap-2">
      <label className="block">Caliente ≥<input type="number" className={`${fieldCls} mt-0.5 w-16`} value={c} onChange={(e) => setC(e.target.value)} /></label>
      <label className="block">Frío ≤<input type="number" className={`${fieldCls} mt-0.5 w-16`} value={f} onChange={(e) => setF(e.target.value)} /></label>
      {changed && <Button size="sm" className="h-7 px-2 text-xs" disabled={saving} onClick={() => onSave(Number(c), Number(f))}>OK</Button>}
    </div>
  );
}
